import * as React from 'react';

import { get as userGet } from 'js/lib/user';

import { useEnterpriseMyPageHeader } from 'bundles/composable-page-header-enterprise/contexts/EnterpriseMyPageHeaderContext';
import LoggedInEnterpriseDesktopMainNav from 'bundles/composable-page-header-enterprise/main-nav-modules/desktop/LoggedInEnterpriseDesktopMainNav';
import LoggedOutEnterpriseDesktopMainNav from 'bundles/composable-page-header-enterprise/main-nav-modules/desktop/LoggedOutEnterpriseDesktopMainNav';
import LoggedInEnterpriseMobileMainNav from 'bundles/composable-page-header-enterprise/main-nav-modules/mobile/LoggedInEnterpriseMobileMainNav';
import LoggedOutEnterpriseMobileMainNav from 'bundles/composable-page-header-enterprise/main-nav-modules/mobile/LoggedOutEnterpriseMobileMainNav';
import DefaultEnterprisePageHeaderProvider from 'bundles/composable-page-header-enterprise/providers/DefaultEnterprisePageHeaderProvider';
import { mergeEnterpriseDataWithBackup } from 'bundles/composable-page-header-enterprise/utils/enterpriseProgramDataMerge';
import GlobalPageHeaderContainer from 'bundles/composable-page-header/components/GlobalPageHeaderContainer';
import GlobalPageHeaderStateProvider from 'bundles/composable-page-header/providers/GlobalPageHeaderStateProvider';
import type {
  EnterpriseProgramFromSlugFragment,
  NaptimeThirdPartyOrganizationFromSlugFragment,
} from 'bundles/enterprise-legacy-learner-home/queries/__generated__/EnterpriseProgramBySlugQuery';

type Props = {
  backupProgram?: EnterpriseProgramFromSlugFragment;
  backupThirdPartyOrg?: NaptimeThirdPartyOrganizationFromSlugFragment;
  hideSearch?: boolean;
  hideExplore?: boolean;
  injectedSearch?: React.ReactNode;
};

const EnterpriseInjectedSearchPageHeader = ({
  backupProgram,
  backupThirdPartyOrg,
  hideSearch,
  hideExplore,
  injectedSearch,
}: Props) => {
  const user = userGet();
  const enterpriseMyPageHeader = useEnterpriseMyPageHeader();

  const pageHeaderDataWithBackup = mergeEnterpriseDataWithBackup(
    enterpriseMyPageHeader,
    backupProgram,
    backupThirdPartyOrg
  );

  const getDesktopNav = () => {
    if (user?.id) {
      return (
        <LoggedInEnterpriseDesktopMainNav
          pageHeaderData={pageHeaderDataWithBackup}
          hideSearch={hideSearch}
          hideExplore={hideExplore}
          injectedSearch={injectedSearch}
        />
      );
    } else {
      return (
        <LoggedOutEnterpriseDesktopMainNav
          pageHeaderData={pageHeaderDataWithBackup}
          hideSearch={hideSearch}
          hideExplore={hideExplore}
          injectedSearch={injectedSearch}
        />
      );
    }
  };
  const getMobileNav = () => {
    if (user?.id) {
      return (
        <LoggedInEnterpriseMobileMainNav
          pageHeaderData={pageHeaderDataWithBackup}
          hideSearch={hideSearch}
          injectedSearch={injectedSearch}
        />
      );
    } else {
      return (
        <LoggedOutEnterpriseMobileMainNav
          pageHeaderData={pageHeaderDataWithBackup}
          hideSearch={hideSearch}
          injectedSearch={injectedSearch}
        />
      );
    }
  };

  const DesktopNavComponent = getDesktopNav();
  const MobileNavComponent = getMobileNav();

  return <GlobalPageHeaderContainer desktopMainNav={DesktopNavComponent} mobileMainNav={MobileNavComponent} />;
};

const EnterpriseInjectedSearchPageHeaderWithProvider = ({
  backupProgram,
  backupThirdPartyOrg,
  hideSearch,
  hideExplore,
  injectedSearch,
}: Props) => {
  return (
    <GlobalPageHeaderStateProvider additionalDefaultPageHeaderHeight={undefined}>
      <DefaultEnterprisePageHeaderProvider>
        <EnterpriseInjectedSearchPageHeader
          backupProgram={backupProgram}
          backupThirdPartyOrg={backupThirdPartyOrg}
          hideSearch={hideSearch}
          hideExplore={hideExplore}
          injectedSearch={injectedSearch}
        />
      </DefaultEnterprisePageHeaderProvider>
    </GlobalPageHeaderStateProvider>
  );
};

export default EnterpriseInjectedSearchPageHeaderWithProvider;
